import { useMemo } from "react";
import type { SanityPageBuilderFaqSection, SanityPageBuilderSection } from "@ouster/shared";
import { FAQBlockView } from "~/features/blocks/inventory/faq";
import { FAQQuestionView } from "~/features/blocks/inventory/faq/faq-question";
import { type Mode, type Theme } from "~/features/ui/get-theme-style";
import { useFaqAnchors } from "~/features/sanity/faq/context";
import { collectFaqEntries, faqSlugMapKey, toFaqSlugMap } from "~/features/sanity/faq/entries";
import { toCraftLinkArray } from "~/features/sanity/link";
import { SanityRichText } from "~/features/sanity/rich-text";
type Props = {
  section: SanityPageBuilderSection & {
    loaded?: boolean;
    sectionContent: SanityPageBuilderFaqSection;
  };
};
export function SanityFaqSection({
  section
}: Props) {
  const {
    sectionContent,
    sectionSettings
  } = section;
  const mode = (sectionSettings?.mode || "light") as Mode;
  const theme = (sectionSettings?.theme || "white") as Theme;

  // Normally provided once per route so anchors stay unique across every FAQ
  // section on the page. Absent in the isolated section-preview harness, where
  // resolving this section alone is correct.
  const pageAnchors = useFaqAnchors();
  const localAnchors = useMemo(() => pageAnchors ? null : toFaqSlugMap(collectFaqEntries([section])), [pageAnchors, section]);
  const anchors = pageAnchors ?? localAnchors;
  return <FAQBlockView anchor={{
    id: sectionSettings?.sectionId?.current || sectionSettings?.anchorName || section._key,
    name: sectionSettings?.anchorName
  }} getItemValue={question => anchors?.get(faqSlugMapKey(section._key, question._key)) ?? question._key ?? ""} hasTopDivider={sectionSettings?.hasTopDivider ?? true} headline={sectionContent.headline} items={sectionContent.questions} link={toCraftLinkArray(sectionContent.link)} mode={mode} renderQuestion={(question, isOpen) => <FAQQuestionView answer={question.answer?.length ? <SanityRichText value={question.answer} /> : undefined} id={anchors?.get(faqSlugMapKey(section._key, question._key)) ?? question._key} isOpen={isOpen} mode={mode} question={question.question} theme={theme} />} sectionProps={{
    "data-sanity-section-key": section._key,
    "data-sanity-section-type": section._type
  }} theme={theme} />;
}